"use client"

import { useState, useEffect, useCallback } from "react"
import { supabase } from "../supabaseClient"
import { useAuth } from "../context/AuthContext"
import "../styles/InstituicoesPage.css"

export default function InstituicoesPage() {
  const { isAdmin, userProfile, papel } = useAuth()
  const [instituicoes, setInstituicoes] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editando, setEditando] = useState(null)
  const [formData, setFormData] = useState({
    nome: "",
    sigla: "",
    endereco: "",
    telefone: "",
    email: "",
    max_usuarios: 5,
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [instituicaoToDelete, setInstituicaoToDelete] = useState(null)
  const [erro, setErro] = useState("")
  const [sucesso, setSucesso] = useState("")

  const carregarInstituicoes = useCallback(async () => {
    setLoading(true)
    setErro("")
    
    console.log("[v0] Carregando instituicoes...")
    
    try {
      // Query sem autenticação do Supabase - apenas com as políticas de RLS
      let query = supabase.from("instituicoes").select("*").order("nome")
      // Se o usuário não for admin, limitar à sua própria instituição
      if (!isAdmin && userProfile?.instituicao_id) {
        query = query.eq("id", userProfile.instituicao_id)
      }
      const { data: instData, error: instError } = await query

      console.log("[v0] Resultado da query instituicoes:", { instData, instError })

      if (instError) {
        console.error("[v0] Erro ao carregar instituicoes:", instError)
        // Se tiver erro de RLS, mostrar mensagem útil
        if (instError.message?.includes("row level security")) {
          setErro("Erro de permissão RLS. Verifique as políticas de RLS no Supabase. Execute o script cleanup_rls.sql")
        } else {
          setErro("Erro ao carregar instituicoes: " + instError.message)
        }
        setInstituicoes([])
      } else {
        // Para cada instituicao, contar usuarios
        const instituicoesComContagem = await Promise.all(
          (instData || []).map(async (inst) => {
            const { count, error: countError } = await supabase
              .from("usuarios")
              .select("*", { count: "exact", head: true })
              .eq("instituicao_id", inst.id)

            return {
              ...inst,
              usuarios_count: countError ? 0 : count || 0,
            }
          })
        )

        console.log("[v0] Instituicoes com contagem:", instituicoesComContagem)
        setInstituicoes(instituicoesComContagem)
      }
    } catch (error) {
      console.error("[v0] Erro ao carregar instituicoes:", error)
      setErro("Erro ao carregar instituicoes: " + error.message)
      setInstituicoes([])
    } finally {
      setLoading(false)
    }
  }, [isAdmin, userProfile?.instituicao_id])

  useEffect(() => {
    console.log("[v0] useEffect - isAdmin:", isAdmin, "userProfile:", userProfile, "papel:", papel)
    
    // Permitir acesso para admin ou se nao houver verificacao de papel ainda
    // Isso permite testar mesmo sem login como admin
    carregarInstituicoes()
  }, [carregarInstituicoes, isAdmin, userProfile, papel])

  const limparMensagens = () => {
    setErro("")
    setSucesso("")
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    limparMensagens()

    // Apenas admin pode criar/editar instituicoes
    if (!isAdmin) {
      setErro("Sem permissao: apenas administradores podem criar ou editar instituicoes.")
      return
    }

    if (!formData.nome.trim()) {
      setErro("O nome da instituicao e obrigatorio.")
      return
    }

    console.log("[v0] Salvando instituicao:", formData)

    try {
      if (editando) {
        console.log("[v0] Atualizando instituicao ID:", editando.id)
        
        const { data, error } = await supabase
          .from("instituicoes")
          .update({
            nome: formData.nome.trim(),
            sigla: formData.sigla.trim() || null,
            endereco: formData.endereco.trim() || null,
            telefone: formData.telefone.trim() || null,
            email: formData.email.trim() || null,
            max_usuarios: formData.max_usuarios,
          })
          .eq("id", editando.id)
          .select()

        console.log("[v0] Resultado update:", { data, error })

        if (error) throw error
        
        if (!data || data.length === 0) {
          throw new Error("Nenhum registro foi atualizado. Execute o script SQL para desabilitar o RLS no Supabase.")
        }
        
        setSucesso("Instituicao atualizada com sucesso!")
      } else {
        console.log("[v0] Inserindo nova instituicao")
        
        const { data, error } = await supabase
          .from("instituicoes")
          .insert([
            {
              nome: formData.nome.trim(),
              sigla: formData.sigla.trim() || null,
              endereco: formData.endereco.trim() || null,
              telefone: formData.telefone.trim() || null,
              email: formData.email.trim() || null,
              max_usuarios: formData.max_usuarios,
            },
          ])
          .select()

        console.log("[v0] Resultado insert:", { data, error })

        if (error) throw error
        
        if (!data || data.length === 0) {
          throw new Error("Nenhum registro foi inserido. Execute o script SQL no Supabase (SQL Editor) para desabilitar o RLS.")
        }
        
        setSucesso("Instituicao cadastrada com sucesso!")
      }

      setShowForm(false)
      setEditando(null)
      setFormData({ nome: "", sigla: "", endereco: "", telefone: "", email: "", max_usuarios: 5 })
      
      // Recarregar lista
      await carregarInstituicoes()
    } catch (error) {
      console.error("[v0] Erro ao salvar instituicao:", error)
      setErro("Erro ao salvar instituicao: " + error.message)
    }
  }

  const handleEdit = (instituicao) => {
    // Apenas admin pode editar
    if (!isAdmin) {
      setErro("Sem permissao: apenas administradores podem editar instituicoes.")
      return
    }

    limparMensagens()
    setEditando(instituicao)
    setFormData({
      nome: instituicao.nome || "",
      sigla: instituicao.sigla || "",
      endereco: instituicao.endereco || "",
      telefone: instituicao.telefone || "",
      email: instituicao.email || "",
      max_usuarios: instituicao.max_usuarios || 5,
    })
    setShowForm(true)
  }

  const confirmDelete = (instituicao) => {
    limparMensagens()
    setInstituicaoToDelete(instituicao)
    setShowDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!instituicaoToDelete) return

    try {
      // Verificar se ha usuarios vinculados
      const { count } = await supabase
        .from("usuarios")
        .select("*", { count: "exact", head: true })
        .eq("instituicao_id", instituicaoToDelete.id)

      if (count && count > 0) {
        setErro(`Nao e possivel excluir. Existem ${count} usuario(s) vinculado(s) a esta instituicao.`)
        setShowDeleteModal(false)
        setInstituicaoToDelete(null)
        return
      }

      const { error } = await supabase
        .from("instituicoes")
        .delete()
        .eq("id", instituicaoToDelete.id)

      if (error) throw error

      setSucesso("Instituicao excluida com sucesso!")
      setShowDeleteModal(false)
      setInstituicaoToDelete(null)
      carregarInstituicoes()
    } catch (error) {
      console.error("[v0] Erro ao excluir instituicao:", error)
      setErro("Erro ao excluir instituicao: " + error.message)
    }
  }

  const handleNovaInstituicao = () => {
    if (!isAdmin) {
      setErro("Sem permissao: apenas administradores podem cadastrar instituicoes.")
      return
    }

    limparMensagens()
    setEditando(null)
    setFormData({ nome: "", sigla: "", endereco: "", telefone: "", email: "", max_usuarios: 5 })
    setShowForm(true)
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Gestao de Instituicoes</h1>
        {isAdmin && (
          <button className="btn-primary" onClick={handleNovaInstituicao}>
            + Nova Instituicao
          </button>
        )}
      </div>

      {erro && <div className="alert alert-error">{erro}</div>}
      {sucesso && <div className="alert alert-success">{sucesso}</div>}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editando ? "Editar Instituicao" : "Nova Instituicao"}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nome da Instituicao *</label>
                <input
                  type="text"
                  placeholder="Ex: Universidade de Luanda"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Sigla</label>
                <input
                  type="text"
                  placeholder="Ex: UL"
                  value={formData.sigla}
                  onChange={(e) => setFormData({ ...formData, sigla: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Endereco</label>
                <textarea
                  placeholder="Endereco completo"
                  value={formData.endereco}
                  onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Telefone</label>
                  <input
                    type="tel"
                    placeholder="+244 xxx xxx xxx"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    placeholder="email@instituicao.ao"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Limite de Usuarios:</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.max_usuarios}
                  onChange={(e) => setFormData({ ...formData, max_usuarios: Math.min(5, Math.max(1, parseInt(e.target.value) || 5)) })}
                />
                <small>Na primeira fase, cada instituicao pode ter no maximo 5 usuarios.</small>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  Salvar
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowForm(false)
                    setEditando(null)
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Confirmar Exclusao</h2>
            <p>
              Tem certeza que deseja excluir a instituicao <strong>{instituicaoToDelete?.nome}</strong>?
            </p>
            <p className="warning-text">Esta acao nao pode ser desfeita!</p>
            <div className="modal-actions">
              <button className="btn-delete" onClick={handleDelete}>
                Confirmar Exclusao
              </button>
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Carregando instituicoes...</div>
      ) : instituicoes.length === 0 ? (
          <div className="empty-state">
          <p>Nenhuma instituicao cadastrada.</p>
          {isAdmin && (
            <button className="btn-primary" onClick={handleNovaInstituicao}>
              Cadastrar primeira instituicao
            </button>
          )}
        </div>
      ) : (
        <div className="cards-grid">
          {instituicoes.map((inst) => (
            <div key={inst.id} className="card">
              <div className="card-header">
                <h3>{inst.nome}</h3>
                {inst.sigla && <span className="badge">{inst.sigla}</span>}
              </div>
              <div className="card-body">
                <p>
                  <strong>Email:</strong> {inst.email || "-"}
                </p>
                <p>
                  <strong>Telefone:</strong> {inst.telefone || "-"}
                </p>
                <p>
                  <strong>Endereco:</strong> {inst.endereco || "-"}
                </p>
                <div className="usuarios-info">
                  <strong>Usuarios:</strong>
                  <span className={inst.usuarios_count >= inst.max_usuarios ? "limit-reached" : ""}>
                    {inst.usuarios_count} / {inst.max_usuarios}
                  </span>
                  {inst.usuarios_count >= inst.max_usuarios && (
                    <span className="badge badge-warning">Limite atingido</span>
                  )}
                </div>
              </div>
              <div className="card-actions">
                {isAdmin ? (
                  <>
                    <button className="btn-edit" onClick={() => handleEdit(inst)}>
                      Editar
                    </button>
                    <button className="btn-delete" onClick={() => confirmDelete(inst)}>
                      Excluir
                    </button>
                  </>
                ) : (
                  <small>Sem autorização para editar/excluir.</small>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
